import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Thermometer, MapPin, Shield, Bell, FileText, QrCode,
  ArrowRight, CheckCircle, Truck, Wifi, Scale, Lock,
  BarChart3, Clock, Users, Smartphone, Snowflake, Sun, AlertTriangle, FlaskConical
} from 'lucide-react';

const mainFeatures = [
  {
    icon: Thermometer,
    title: 'Real-time Temperature Monitoring',
    description: 'Continuous monitoring of 2-8°C cold chain requirements with precision sensors. Get instant alerts when temperature deviates from safe range. Perfect for insulin, Ozempic, and other temperature-sensitive medications.',
    benefits: ['±0.5°C accuracy', 'Configurable thresholds', 'Historical logs', 'Trend analysis'],
    image: 'https://images.unsplash.com/photo-1745939931505-da371555a603?w=600&h=400&fit=crop',
    color: 'from-blue-500 to-blue-600'
  },
  {
    icon: MapPin,
    title: 'GPS Location Tracking',
    description: 'Track your delivery vehicles in real-time with precise GPS coordinates. Monitor routes, stops, and delivery progress on an interactive map.',
    benefits: ['Live location updates', 'Route history', 'Geofencing alerts', 'ETA predictions'],
    image: 'https://images.unsplash.com/photo-1639829743267-cb0341cf341e?w=600&h=400&fit=crop',
    color: 'from-green-500 to-green-600'
  },
  {
    icon: Shield,
    title: 'Tamper Detection & Security',
    description: 'Advanced sensors detect bag opening, weight changes, and unauthorized access. Ensure product integrity from warehouse to delivery.',
    benefits: ['Bag open/close detection', 'Weight monitoring', 'Seal verification', 'Security alerts'],
    image: 'https://images.unsplash.com/photo-1593491034932-844ab981ed7c?w=600&h=400&fit=crop',
    color: 'from-red-500 to-red-600'
  },
  {
    icon: FileText,
    title: 'Compliance & Reporting',
    description: 'Generate GDP-compliant reports automatically. Complete audit trails for regulatory inspections and quality assurance.',
    benefits: ['PDF/Excel exports', 'Temperature graphs', 'Delivery certificates', 'Audit trails'],
    image: 'https://images.unsplash.com/photo-1698506455775-42635fdd16a2?w=600&h=400&fit=crop',
    color: 'from-purple-500 to-purple-600'
  }
];

const additionalFeatures = [
  {
    icon: QrCode,
    title: 'QR Code Verification',
    description: 'Receivers can verify delivery authenticity and view compliance reports by scanning a QR code - no login required.'
  },
  {
    icon: Bell,
    title: 'Smart Alerts',
    description: 'Instant notifications for temperature breaches, delivery status changes, and critical events via dashboard and email.'
  },
  {
    icon: Scale,
    title: 'Weight Monitoring',
    description: 'Track package weight changes throughout the journey to detect tampering or product removal.'
  },
  {
    icon: Lock,
    title: 'Secure Access',
    description: 'Role-based access control with multi-tenant architecture. Each organization sees only their data.'
  },
  {
    icon: BarChart3,
    title: 'Analytics Dashboard',
    description: 'Comprehensive dashboards showing compliance rates, delivery performance, and trend analysis.'
  },
  {
    icon: Users,
    title: 'Multi-tenant Platform',
    description: 'Manage multiple distribution partners and locations from a single platform with isolated data.'
  },
  {
    icon: Smartphone,
    title: 'Mobile Ready',
    description: 'Responsive design works on any device. Drivers can update status and scan QR codes on mobile.'
  },
  {
    icon: Wifi,
    title: 'IoT Integration',
    description: 'Connect with our proprietary IoT devices or integrate with existing hardware via open APIs.'
  }
];

const complianceStandards = [
  { name: 'GDP', description: 'Good Distribution Practice' },
  { name: 'WHO', description: 'World Health Organization Guidelines' },
  { name: 'D&C Act', description: 'Drugs & Cosmetics Act' },
  { name: 'Schedule M', description: 'Manufacturing Standards' }
];

// Pharmaceutical Products by Temperature Range
const temperatureRanges = [
  {
    id: 'refrigerated',
    icon: Thermometer,
    range: '2–8°C',
    title: 'Refrigerated Medicines',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    categories: [
      {
        name: 'Insulin',
        products: ['Lantus', 'Humalog', 'Novorapid', 'Actrapid']
      },
      {
        name: 'Vaccines',
        products: ['Hepatitis B', 'Tetanus', 'Rabies', 'Influenza', 'HPV vaccine']
      },
      {
        name: 'Fertility Medicines (IVF)',
        products: ['Gonal-F', 'Menopur', 'Follistim', 'Ovitrelle']
      },
      {
        name: 'Biologics / Monoclonal Antibodies',
        products: ['Humira (adalimumab)', 'Enbrel (etanercept)', 'Rituximab']
      },
      {
        name: 'Growth Hormones',
        products: ['Somatropin injections']
      }
    ],
    risks: ['Protein degradation', 'Loss of potency', 'Reduced therapeutic effect', 'Increased adverse reactions']
  },
  {
    id: 'controlled',
    icon: Sun,
    range: '15–25°C',
    title: 'Controlled Room Temperature',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    categories: [
      {
        name: 'Oral Medications',
        products: ['Antibiotics tablets', 'Painkillers', 'Anti-hypertensives', 'Metformin', 'Most oral medications']
      }
    ],
    risks: ['Heat sensitivity', 'Humidity damage', 'Chemical degradation', 'Reduced shelf life']
  },
  {
    id: 'frozen',
    icon: Snowflake,
    range: '–20°C or below',
    title: 'Frozen Pharmaceuticals',
    color: 'from-indigo-500 to-purple-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    categories: [
      {
        name: 'Ultra-Cold Storage',
        products: ['mRNA vaccines', 'Advanced biologics', 'Laboratory reagents', 'Plasma samples']
      }
    ],
    risks: ['Complete loss of efficacy', 'Structural damage', 'Safety concerns', 'Regulatory non-compliance']
  },
  {
    id: 'diagnostic',
    icon: FlaskConical,
    range: '2–8°C / Controlled',
    title: 'Diagnostic Samples',
    color: 'from-teal-500 to-green-500',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    categories: [
      {
        name: 'Medical Samples',
        products: ['Blood samples', 'Biopsy samples', 'Reagents', 'Tissue samples']
      }
    ],
    risks: ['Sample degradation', 'Invalid test results', 'Misdiagnosis risk', 'Regulatory issues']
  }
];

export default function FeaturesPage() {
  return (
    <div data-testid="features-page" className="min-h-screen bg-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-6 bg-blue-100 text-blue-700 border-blue-200">
              PLATFORM FEATURES
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Complete Cold Chain
              <span className="text-primary"> Monitoring Solution</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Track<span className="text-red-500">0</span>tic provides everything you need to ensure 
              temperature compliance, secure deliveries, and generate audit-ready documentation 
              for pharmaceutical logistics.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg shadow-lg"
              >
                Request a Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Features with Images */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {mainFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`grid lg:grid-cols-2 gap-12 items-center mb-24 last:mb-0 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {feature.description}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {feature.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} rounded-2xl transform rotate-3 opacity-20`}></div>
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="relative rounded-2xl shadow-xl w-full object-cover aspect-video"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              MORE FEATURES
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything Else You Need
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Beyond core monitoring, Track<span className="text-red-500">0</span>tic offers a complete 
              suite of tools for managing your pharmaceutical logistics.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pharmaceutical Products by Temperature Range */}
      <section className="py-20 bg-white" data-testid="temperature-ranges-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              PRODUCTS WE PROTECT
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pharmaceutical Products by
              <span className="text-primary"> Temperature Range</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Track<span className="text-red-500">0</span>tic monitors the full spectrum of temperature-sensitive 
              pharmaceuticals, from refrigerated insulin to ultra-cold mRNA vaccines.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {temperatureRanges.map((range, index) => (
              <motion.div
                key={range.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-2xl border-2 ${range.borderColor} overflow-hidden`}
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${range.color} px-6 py-4`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <range.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold text-white">{range.title}</h3>
                        <p className="text-white/80 text-sm">{range.range}</p>
                      </div>
                    </div>
                    <Badge className="bg-white/20 text-white border-white/30 font-mono">
                      {range.range}
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <div className={`${range.bgColor} p-6`}>
                  <div className="space-y-4">
                    {range.categories.map((category) => (
                      <div key={category.name}>
                        <h4 className="font-semibold text-gray-900 text-sm mb-2">{category.name}</h4>
                        <div className="flex flex-wrap gap-2">
                          {category.products.map((product) => (
                            <span 
                              key={product}
                              className="text-xs px-2.5 py-1 bg-white rounded-full text-gray-700 border border-gray-200"
                            >
                              {product}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Risk Warning */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-semibold text-amber-700">Risk if Temperature Breaks:</span>
                        <p className="text-xs text-gray-600 mt-1">
                          {range.risks.join(' • ')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Standards */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              COMPLIANCE
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Built for Regulatory Compliance
            </h2>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Track<span className="text-red-300">0</span>tic helps you meet all major pharmaceutical 
              distribution compliance standards.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {complianceStandards.map((standard, index) => (
              <motion.div
                key={standard.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-2">
                  {standard.name}
                </h3>
                <p className="text-sm text-blue-200">
                  {standard.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact us for pricing information and a personalized demo of 
              Track<span className="text-red-500">0</span>tic for your organization.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg shadow-lg"
              >
                Contact for Pricing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
